import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, ArrowUpRight, Linkedin, Mail, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { trackEvent } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const GROUPS = [
  {
    label: "Product",
    links: [
      { label: "Engines", href: "#engines", testid: "footer-link-tools" },
      { label: "All engines", href: "/engines", testid: "footer-link-engines" },
      { label: "Formats", href: "#formats", testid: "footer-link-formats" },
      { label: "Pricing", href: "#pricing", testid: "footer-link-pricing" },
    ],
  },
  {
    label: "Company",
    links: [
      { label: "Enterprise", href: "#enterprise", testid: "footer-link-enterprise" },
      { label: "Contact", href: "#contact", testid: "footer-link-contact" },
      { label: "What's new", href: "/changelog", testid: "footer-link-changelog" },
    ],
  },
  {
    label: "Compare",
    links: [
      { label: "Smallpdf alternative", href: "/smallpdf-alternative", testid: "footer-link-smallpdf-alt" },
      { label: "iLovePDF alternative", href: "/ilovepdf-alternative", testid: "footer-link-ilovepdf-alt" },
      { label: "Acrobat alternative", href: "/adobe-acrobat-alternative", testid: "footer-link-acrobat-alt" },
      { label: "DocuSign alternative", href: "/docusign-alternative", testid: "footer-link-docusign-alt" },
      { label: "Data-room checklist", href: "/prepare-data-room-documents", testid: "footer-link-dataroom" },
      { label: "Redact for diligence", href: "/redact-pdf-for-due-diligence", testid: "footer-link-diligence" },
      { label: "Secure PDF tools", href: "/secure-pdf-tools", testid: "footer-link-secure" },
    ],
  },
  {
    label: "Account",
    links: [
      { label: "Sign in", href: "/login", testid: "footer-link-signin" },
      { label: "Workspace", href: "/workspace", testid: "footer-link-workspace" },
      { label: "Manage subscription", href: "/billing", testid: "footer-link-billing" },
    ],
  },
];

const SOCIALS = [
  { label: "X", href: "https://x.com/doclyx", icon: X, testid: "footer-social-x" },
  { label: "LinkedIn", href: "https://www.linkedin.com/company/doclyx", icon: Linkedin, testid: "footer-social-linkedin" },
  { label: "Email", href: "mailto:hello@doclyx.com", icon: Mail, testid: "footer-social-mail" },
];

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const { pathname } = useLocation();
  const hrefFor = (href) => (pathname === "/" || !href.startsWith("#") ? href : `/${href}`);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email || state === "loading") return;
    setState("loading");
    try {
      await axios.post(`${API}/subscribe`, { email });
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <footer id="contact" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-12">
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1.2, ease: EASE }}
          className="font-medium tracking-tight leading-tight text-3xl md:text-4xl lg:text-5xl text-ink max-w-xl mb-10 md:mb-20"
          data-testid="footer-headline"
        >
          Ready to close the deal<span className="text-azure">.</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 lg:mb-20">
          <div className="lg:col-span-6">
            <Logo />
            <p className="text-base text-fog leading-relaxed mt-6 max-w-sm">
              Precision document infrastructure for high-stakes work. Choose a plan, unlock the workspace,
              and keep every file moving toward close.
            </p>
            <div className="flex items-center gap-4 mt-7 flex-wrap">
              <a href={hrefFor("#pricing")} data-testid="footer-plan-cta" onClick={() => trackEvent("cta_click", { source: "footer" })}
                className="inline-flex items-center gap-2 v4-btn-primary h-10 px-5 rounded-full text-sm font-semibold transition-colors duration-300">
                Choose your plan <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
              </a>
              <div className="flex items-center gap-2" data-testid="footer-socials">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target={s.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    aria-label={s.label}
                    data-testid={s.testid}
                    className="w-10 h-10 rounded-full border border-hairline flex items-center justify-center text-fog hover:text-ink hover:border-ink/30 transition-colors duration-300"
                  >
                    <s.icon className="w-4 h-4" strokeWidth={1.5} />
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="lg:col-span-6 lg:pl-16">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-mist block mb-6">Deal memo</span>
            {state === "done" ? (
              <p className="font-mono text-sm text-azure" data-testid="subscribe-success">You're on the list — check your inbox.</p>
            ) : (
              <form onSubmit={subscribe} className="flex items-end gap-4 border-b border-edge focus-within:border-azure pb-3 transition-colors duration-300" data-testid="subscribe-form">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Work email"
                  data-testid="subscribe-email-input"
                  className="flex-1 bg-transparent text-lg text-ink placeholder:text-mist focus:outline-none min-w-0"
                />
                <button
                  type="submit"
                  disabled={state === "loading"}
                  data-testid="subscribe-submit-button"
                  className="text-ink hover:text-azure hover:translate-x-1 transition-all duration-300 pb-0.5"
                  aria-label="Subscribe"
                >
                  {state === "loading" ? <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <ArrowRight className="w-5 h-5" strokeWidth={1.5} />}
                </button>
              </form>
            )}
            {state === "error" && (
              <p className="text-xs text-[#FF3333] mt-3" data-testid="subscribe-error">Failed to submit — try again.</p>
            )}
          </div>
        </div>

        <div className="border-t border-hairline pt-10 mb-10 grid grid-cols-2 md:grid-cols-3 gap-8" data-testid="footer-groups">
          {GROUPS.map((g) => (
            <div key={g.label} data-testid={`footer-group-${g.label.toLowerCase()}`}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist block mb-4">{g.label}</span>
              <div className="space-y-3">
                {g.links.map((l) => (
                  <a
                    key={l.href}
                    href={hrefFor(l.href)}
                    data-testid={l.testid}
                    className="group flex items-center gap-1 text-sm text-fog hover:text-ink transition-colors duration-300 w-fit"
                  >
                    {l.label}
                    <ArrowUpRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-60 group-hover:translate-x-0 transition-all duration-300" strokeWidth={2} />
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-hairline pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">© 2026 Doclyx</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mist">Precision document infrastructure · Zero retention</span>
        </div>
      </div>
    </footer>
  );
};
